import styled from '@emotion/styled';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { GraphDirectoryItem } from '@/redux/slices/slice_types';
import { selectGraphsStatus } from '@/redux/selectors/dashboardSelectors';
import { lighten, darken } from '@mui/material';
import { useAppSelector } from '@/redux/hooks';

interface PaneProps {
  pane: GraphDirectoryItem;
}

export type PaneDragType = {
  index: number;
};

const Pane = ({ pane }: PaneProps) => {
  const { editable, condensed } = useAppSelector(selectGraphsStatus);
  return (
    <Wrapper>
      <Widget>
        {editable ? (
          <EditableWrapper condensed={+condensed} disabled={+!!pane.fullRow}>
            <LabelLine condensed={+condensed}>
              <span>{pane.label}</span>
              {!pane.fullRow && <StyledDragIcon icon="grip" height={24} disabled={+!!pane.fullRow} />}
            </LabelLine>
            {!condensed && (
              <ChartRender full={+!!pane.fullRow} editable selfmanaged={+!!pane.selfManagedEditableView}>
                {pane.render()}
              </ChartRender>
            )}
          </EditableWrapper>
        ) : (
          pane.render()
        )}
      </Widget>
    </Wrapper>
  );
};

export default Pane;

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
`;

const LabelLine = styled.div<{ condensed: number }>`
  display: flex;
  justify-content: ${({ condensed }) => (condensed ? 'center' : 'space-between')};
`;

const EditableWrapper = styled.div<{ condensed: number; disabled: number }>`
  position: relative;
  background: ${({ theme }) => {
    return theme.palette.mode === 'dark'
      ? `linear-gradient(212.15deg, ${darken(theme.palette.primary.main, 0.7)} 15%, ${darken(theme.palette.primary.main, 0.6)} 75%)`
      : lighten(theme.palette.primary.main, 0.8);
  }};
  padding: ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  text-align: center;

  font-size: 1.2rem;
  height: 100%;
  display: flex;
  flex-direction: column;

  > div:first-of-type {
    span {
      padding: ${({ theme, condensed }) => (!condensed ? theme.spacing(0.5, 0, 2) : '0px')};
    }
  }

  cursor: grab;

  ${({ theme, disabled }) =>
    !disabled &&
    `
    &:hover {
      background-color: ${theme.palette.mode === 'dark' ? theme.palette.primary.main : lighten(theme.palette.primary.main, 0.95)};

      svg {
        color: ${lighten(theme.palette.primary.main, 0.5)};
      }
    }
  `}
`;

const ChartRender = styled.div<{ editable?: boolean; full: number; selfmanaged: number }>`
  cursor: default;
  height: 100%;
  opacity: ${({ editable, selfmanaged }) => (editable && !selfmanaged ? 0.4 : 1)};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, full }) => (full ? 'transparent' : theme.palette.background.paper)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  pointer-events: ${({ editable, selfmanaged }) => (editable && !selfmanaged ? 'none' : 'auto')};

  ${({ editable }) =>
    editable &&
    `
  div {

    > .no-data {
      display: none;
    }
  }
  
  `}
`;

const Widget = styled.div`
  transition: all 0.2s ease-in-out;
  flex-grow: 1;
`;

const StyledDragIcon = styled(PolarisIcon)<{ disabled: number }>`
  position: absolute;
  right: 10px;
  top: 15px;
  color: ${({ theme, disabled }) => (disabled ? theme.palette.text.disabled : theme.palette.primary.main)};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;
